import { ComboboxItem } from "@ariakit/react";
import {
  Cube,
  CurrencyDollar,
  GraduationCap,
  GridNine,
  Lifebuoy,
  Password,
  Plus,
  RocketLaunch,
  Sparkle,
  Star,
  User,
} from "@phosphor-icons/react";
import { Badge } from "@replicate/ui";
import { groupBy } from "lodash-es";
import { matchSorter } from "match-sorter";
import { useId, useMemo, useState } from "react";
import {
  CollectionSearchResultItem,
  ModelSearchResultItem,
  SearchPageResultItem,
} from ".";
import { route } from "../../urls";
import GlyphLoader from "../glyph-loader";
import { useSearchPrototype } from "../search-prototype/hooks";
import {
  Combobox,
  ComboboxPanel,
  ComboboxPopover,
  ComboboxProvider,
  ComboboxTab,
  ComboboxTabList,
} from "./combobox";
import { useSearch } from "./hooks";

function getTabId(category: string, prefix: string) {
  return `${prefix}/${category}`;
}

function getCategory(tabId: string, prefix: string) {
  return tabId.replace(`${prefix}/`, "");
}

const defaultPages = [
  {
    name: "Playground (beta)",
    href: "https://replicate.com/playground",
    icon: GridNine,
  },
  {
    name: "Models",
    href: route("model_list"),
    icon: Cube,
  },
  {
    name: "Predictions",
    href: route("prediction_list"),
    icon: Sparkle,
  },
  {
    name: "Trainings",
    href: route("training_list"),
    icon: GraduationCap,
  },
  {
    name: "Deployments",
    href: route("deployment_list"),
    icon: RocketLaunch,
  },
  {
    name: "Billing",
    href: route("account_billing_settings"),
    icon: CurrencyDollar,
  },
  {
    name: "Support",
    href: route("support"),
    icon: Lifebuoy,
  },
  {
    name: "API Tokens",
    href: route("account_api_token_settings"),
    icon: Password,
  },
  {
    name: "Create model",
    href: route("model_create"),
    icon: Plus,
  },
];

const categories = ["All", "Models", "Collections", "Pages"];

export function TabbedQuickSearchMenu({ username }: { username: string }) {
  const prefix = useId();
  const [searchValue, setSearchValue] = useState("");
  const [tabId, setTabId] = useState(getTabId("All", prefix));

  // Use prototype endpoint for models
  const modelSearchResults = useSearchPrototype({
    query: searchValue,
    enabled: true,
  });

  // Use existing endpoint for collections
  const fallbackSearchResults = useSearch({
    query: searchValue,
    entities: ["collections"],
  });

  const isLoading =
    modelSearchResults.isPending ||
    modelSearchResults.isFetching ||
    fallbackSearchResults.isPending ||
    fallbackSearchResults.isFetching;

  const modelResults = useMemo(() => {
    if (!modelSearchResults.isSuccess) {
      return [];
    }

    return modelSearchResults.data.results.map((result) => {
      const [username, name] = result.name.split("/");
      return {
        username,
        name,
        description: result.description,
        run_count: result.run_count.toLocaleString(),
        owner_avatar: { kind: "organization", url: result.avatar_url },
        latest_enabled_version_id: null,
        hardware: "",
        is_official_model: result.is_official,
        nwo: result.name,
      };
    });
  }, [modelSearchResults]);

  const collectionResults = useMemo(() => {
    if (!fallbackSearchResults.isSuccess) {
      return [];
    }
    return fallbackSearchResults.data.collections;
  }, [fallbackSearchResults]);

  const pageResults = useMemo(() => {
    const pages = [
      ...defaultPages,
      {
        name: "Stars",
        href: route("star_list", { username }),
        icon: Star,
      },
      {
        name: "Profile",
        href: route("account_detail", { username }),
        icon: User,
      },
    ];
    return matchSorter(pages, searchValue ?? "", { keys: ["name"] });
  }, [searchValue, username]);

  const matches = useMemo(() => {
    const allResults = [
      ...modelResults.map((item) => ({
        ...item,
        category: "Models",
        type: "model",
      })),
      ...collectionResults.map((item) => ({
        ...item,
        category: "Collections",
        type: "collection",
      })),
      ...pageResults.map((item) => ({
        ...item,
        category: "Pages",
        type: "page",
      })),
    ];

    const groups = groupBy(allResults, "category");
    groups.All = allResults;
    groups.Models = groups.Models || [];
    groups.Collections = groups.Collections || [];
    groups.Page = groups.Page || [];
    groups.Deployments = groups.Deployments || [];

    return groups;
  }, [modelResults, collectionResults, pageResults]);

  const currentPages = matches[getCategory(tabId, prefix)] || [];

  return (
    <div>
      <ComboboxProvider
        defaultTabId={tabId}
        onTabChange={setTabId}
        onSearch={setSearchValue}
      >
        <Combobox
          className="pl-7 w-full bg-r8-gray-2 px-2 py-1.5 text-r8-gray-12 text-r8-sm border border-transparent focus:ring-black focus:dark:ring-white focus:outline-none focus:ring-1 focus:border-r8-gray-12"
          placeholder="Search 'best image models' or 'text to image'"
          loading={isLoading && Boolean(searchValue)}
        />
        <ComboboxPopover aria-label="Pages">
          <ComboboxTabList aria-label="Categories">
            {categories.map((category) => {
              const currentPages = matches[category];
              return (
                <ComboboxTab
                  key={category}
                  id={getTabId(category, prefix)}
                  disabled={!currentPages?.length}
                >
                  <div className="flex items-center gap-2">
                    <span>{category}</span>
                    <div>
                      <Badge rounded size="sm">
                        {currentPages?.length || 0}
                      </Badge>
                    </div>
                  </div>
                </ComboboxTab>
              );
            })}
          </ComboboxTabList>
          <ComboboxPanel>
            {searchValue && isLoading ? (
              <div className="m-2 p-2 flex gap-4 items-center bg-r8-gray-2 border border-r8-gray-5">
                <GlyphLoader />
                <p className="text-r8-sm text-r8-gray-11">Searching...</p>
              </div>
            ) : null}

            {!Object.values(matches).some((category) => category.length > 0) &&
            searchValue &&
            !isLoading ? (
              <div className="p-3">
                <p className="text-r8-sm text-r8-gray-11">
                  No results found for &quot;<strong>{searchValue}</strong>
                  &quot;
                </p>
              </div>
            ) : null}

            {currentPages.map((item, i) => {
              if (item.type === "model") {
                const modelItem = item as any;
                return (
                  <ComboboxItem
                    key={`${modelItem.username}-${modelItem.name}-${i}`}
                    render={
                      <ModelSearchResultItem condensed model={modelItem} />
                    }
                  />
                );
              }
              if (item.type === "collection") {
                const collectionItem = item as any;
                return (
                  <ComboboxItem
                    key={`${collectionItem.slug}-${i}`}
                    render={
                      <CollectionSearchResultItem collection={collectionItem} />
                    }
                  />
                );
              }
              if (item.type === "page") {
                const pageItem = item as any;
                return (
                  <ComboboxItem
                    key={`${pageItem.href}-${i}`}
                    render={
                      <SearchPageResultItem
                        icon={pageItem.icon}
                        href={pageItem.href}
                      >
                        {pageItem.name}
                      </SearchPageResultItem>
                    }
                  />
                );
              }
              return null;
            })}
          </ComboboxPanel>
        </ComboboxPopover>
      </ComboboxProvider>
    </div>
  );
}
